import { Model } from '@vuex-orm/core'
import { Request } from '@vuex-orm/plugin-axios'
import CancellationPolicy from '@/models/CancellationPolicy'
import PaymentPolicy from '@/models/PaymentPolicy'
import RatePlanPrice from '@/models/RatePlanPrice'
import { getStringFromArray, convertHtml, getCode } from '@/lang'
import config from '@/utils/config'
import AppState from '@/utils/app.state'
import { LangedItem } from './common/commons'

export default class RatePlan extends Model {
  declare id: number | null
  declare name?: LangedItem[]
  declare isPublic: boolean
  declare description: LangedItem[]
  declare pmsMatchingCode?: string
  declare startDate?: string
  declare endDate?: string
  declare images?: any // todo
  declare currency: string
  declare pricingType: string
  declare ratePlanID: string
  declare ratePlanPrices: RatePlanPrice
  declare selectedBoard: number
  declare paymentPolicyID: number
  declare isBundledOffer: boolean
  declare paymentPolicy: PaymentPolicy
  declare cancellationPolicyID: number
  declare cancellationPolicy: CancellationPolicy
  declare cancellationPolicyDescriptions: ({ languageCode: string } & Record<string, string>)[]
  declare internalName: string

  static entity = 'ratePlans'

  static fields() {
    return {
      id: this.number(0).nullable(),
      name: this.attr(null),
      isPublic: this.boolean(false),
      description: this.attr(null),
      pmsMatchingCode: this.string(null),
      startDate: this.string(null),
      endDate: this.string(null),
      images: this.attr(null),
      currency: this.attr(null),
      pricingType: this.attr(null),
      ratePlanID: this.attr(null),
      ratePlanPrices: this.hasOne(RatePlanPrice, 'ratePlanID'),
      selectedBoard: this.number(0),
      paymentPolicyID: this.number(0),
      isBundledOffer: this.boolean(false),
      paymentPolicy: this.belongsTo(PaymentPolicy, 'paymentPolicyID'),
      cancellationPolicyID: this.number(0),
      cancellationPolicy: this.belongsTo(CancellationPolicy, 'cancellationPolicyID'),
      cancellationPolicyDescriptions: this.attr(null),
      internalName: this.attr(null)
    }
  }

  get friendlyName() {
    return this.name && this.name[0] && this.name[0].value
  }

  get cancellationPolicyName() {
    return getStringFromArray(this.cancellationPolicyDescriptions, true, 'friendlyName')
  }

  static mutators() {
    return {
      descriptions(value: any[]) {
        if (!value || !value.length) { return null }

        return value.map((res) => {
          return {
            ...res,
            description: convertHtml(res.description),
            friendlyName: convertHtml(res.friendlyName)
          }
        })
      },

      cancellationPolicyDescriptions(value: any[]) {
        if (!value || !value.length) { return }

        return value.map((res) => {
          return {
            ...res,
            friendlyName: convertHtml(res.friendlyName),
            description: convertHtml(res.description)
          }
        })
      },

      paymentPolicyDescriptions(value: any[]) {
        if (!value || !value.length) { return }

        return value.map((res) => {
          return {
            ...res,
            friendlyName: convertHtml(res.friendlyName),
            description: convertHtml(res.description)
          }
        })
      },

      multimedia(value: any[]) {
        if (!value || !value.length) { return }

        return value.map((res) => {
          let pattern = new RegExp('^(https?|http)://')
          if (!pattern.test(res.url)) {
            return { url: config.ratePlanMediaUrl }
          } else {
            return res
          }
        })
      }
    }
  }

  static apiConfig = {
    actions: {
      fetch(this: Request) {
        return this.post(
          '/content/v2/rateplans/getbypropertyids',
          {
            propertyIDs: [AppState.currentProperty],
            getImages: true,
            getDescriptions: true,
            languageCodes: [getCode()]
          }
        )
      }
    }
  }
}
